/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat, novacuum.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import Modal, { type IInternalModalAttrs } from 'flarum/common/components/Modal';
import app from 'flarum/forum/app';
import type Mithril from 'mithril';
import LinkedAccounts from './LinkedAccounts';
// Type-only: the caller (`utils/Auth`) passes our own PromiseAbortController,
// not the DOM `AbortController`. Both expose `abort()`, so the mistyping was
// invisible at runtime — `app.modal.show()` widens attrs to Record<string, unknown>.
import type PromiseAbortController from '../utils/PromiseAbortController';

export interface AuthModalAttrs extends IInternalModalAttrs {
	title?: Mithril.Children;
	alertText?: Mithril.Children;
	abortController?: PromiseAbortController;
}

export default class AuthModal extends Modal<AuthModalAttrs> {
	success?: boolean;

	oninit(vnode: Mithril.Vnode<AuthModalAttrs, this>) {
		super.oninit(vnode);

		// Make sure that next time the modal is opened, it will be in the initial state
		delete app.fof_oauth_linkingInProgress;
	}

	onremove(vnode: Mithril.VnodeDOM<AuthModalAttrs, this>) {
		super.onremove(vnode);

		if (!this.success) {
			this.attrs.abortController?.abort();
		}
	}

	className() {
		return 'Modal--small AuthModal';
	}

	title(): Mithril.Children {
		const { title } = this.attrs;

		if (title) {
			return title;
		} else {
			if (app.session.user!.attribute('linkedShop')) {
				return app.translator.trans('glowingblue-orell-thalia-common.forum.auth.title_reauthenticate');
			} else {
				return app.translator.trans('glowingblue-orell-thalia-common.forum.auth.title_link');
			}
		}
	}

	content(): Mithril.Children {
		let { alertText } = this.attrs;

		if (!alertText) {
			// Check if already linked or not
			const brandName = app.forum.attribute('orellthalia_brandName');
			const communityName = app.forum.attribute('orellthalia_communityName');

			if (app.session.user!.attribute('linkedShop')) {
				alertText = app.translator.trans('glowingblue-orell-thalia-common.forum.auth.body_reauthenticate', { brandName });
			} else {
				alertText = app.translator.trans('glowingblue-orell-thalia-common.forum.auth.body_link', { communityName, brandName });
			}
		}

		return (
			<div className="Modal-body">
				{alertText && <p>{alertText}</p>}
				<LinkedAccounts
					user={app.session.user!}
					hideManage={true}
					onSuccess={() => {
						this.success = true;
						this.hide();
					}}
				/>
			</div>
		);
	}
}
